/**
 * Declare types in here which are for global application usage
 */

import { SelectOption } from '../components/common/Dropdown/types';

export type AnalysisDisplayOptionSetting = {
  index: string;
  isMandatory: boolean;
  isReadonly: boolean;
};

export type Pipeline = {
  id?: string;
  name: string;
  description?: string;
  analysisDisplayOptions: AnalysisDisplayOption[];
  stages: OpportunityStage[];
  inUse?: boolean;
  quotationEnabled: boolean;
};

export type AnalysisOption = {
  index: string;
  shortName: string;
  fullName: string;
};

export type AnalysisDisplayOption = AnalysisDisplayOptionSetting & {
  shortName: string;
  fullName: string;
};


export type HttpStatusCodeResponse = { 
  statusCode: number;
  id: string;
};

export type OpportunityStage = {
    id: string | null;
    name: string;
    prospect: number;
    rank: number;
    closed: boolean;
    colour: string;
    defaultStatus: OpportunityState;
    inUse: boolean;
    quotationStage: boolean;
    wonStage: boolean;
};

export enum OpportunityState {
  Active = 0,
  Closed = 1
};


export type ProjectService = {
  id: number;
  name: string;
  usageCount: number;
  isDefault: boolean;
  allowedToDelete: boolean;
};

export type CreateProjectService = {
  name: string;
};

export enum UserDefinedItemType {
  Company = 0,
  Contact = 1,
  Product = 2,
  Opportunity = 3,
  History = 5,
  Project = 6
};

export enum UserDefinedDataType {
  String = 0,
  Integer = 1,
  Float2 = 2,
  Float4 = 3,
  Float6 = 4,
  Date = 5,
  Boolean = 6
};

export enum UserDefinedSortColumn
{
    Name,
    CategoryName,
    OwnerName,
    Lookup,
    DataType,
    Subscriptions
};

export enum UserRole {
  Maintenance = 0,
  EditMyVecta = 1,
  Admin = 2,
  UserAdmin = 3,
  EnquiriesAvailable = 4,
  CRMEditImages = 5,
  ExportTasks = 6,
  ExportOpportunities = 7,
  ExportProjects = 8,
  ExportEnquiries = 9,
  ExportAlerts = 10,
  ExportUsers = 11,
  PrintTasks = 12,
  PrintSalesDashboard = 13,
  PrintHomeDashboard = 14,
  PrintCompany = 15,
  DeleteContact = 16,
  DeleteProspect = 17,
  MergeProspect = 18,
  MergeCustomer = 19,
  MergeContact = 20,
  MoveContact = 21,
  DeleteDocument = 22,
  DeleteAnyTasks = 23,
  DeleteOwnTasks = 24,
  DeleteOpportunity = 25,
  DeleteProject = 26,
  RollbackRevision = 27,
  EditStructure = 28,
  EditCompanyType = 29,
  SuperUser = 30,
  ScheduledReports = 31
};

export type SubCount = {
  userSubCount: number;
  groupSubCount: number;
  globalSubCount: number;
  userCatSubCount: number;
  groupCatSubCount: number;
  globalCatSubCount: number;
};

export type UserDefinedItem = {
  id: string;
  name: string;
  itemType: UserDefinedItemType;
  lookup: boolean;
  dataType: string;
  categoryId: string;
  categoryName: string;
  ownerId: string;
  ownerName: string;
  ownerEnabled: boolean;
  subCounts: SubCount;
};

export type CreateUserDefinedItem = {
  name: string;
  description: string;
  categoryId: string;
  itemType: UserDefinedItemType;
  dataType: UserDefinedDataType;
  ownerId: string;
  lookup: boolean;
  lookupValues: string[];
};

export type UpdateUserDefinedItem = {
  id: string;
  name: string;
  description: string;
  categoryId: string;
  itemType: UserDefinedItemType;
  dataType: UserDefinedDataType;
  ownerId: string;
  lookup: boolean;
  lookupValues: string[];
};

export enum DisplayStyleType {
  dsFunnelChart = 0,
  dsGrid = 1
};

export enum FilterType {
  TextBox = 0,
  DropDown = 1,
  MultiSelect = 2,
  DateRange = 3
};

export enum LibraryItemType {
  PipelineCrm = 0,
};

export enum PipelineReportColumn
{
  Company,
  User,
  Opportunity,
  Stage,
  Expected,
  Weighted,
  Active,
  Closed,
  Total
}

export enum PipelineReportDisplayBy
{
  Company,
  User,
  Stage,
  Opportunity
}

export enum DateFilterMode
{
  Fixed,
  RollingDay,
  RollingWeek,
  RollingMonth,
  RollingYear
}

export type CrmItemViewModel = {
  pipelineId: string;
  pipelineStages: string[];
  libraryItemType: string;
  displayBy: string;
  displayStyle: string;
  group: string;
  currentUserOnly: boolean;
  name: string;
  noResult: number;
  dateFilterMode: string;
  sortColumn: string;
  sortDesc: boolean;
  visibleColumns: string[];
  dynamicFilters: DynamicFilterViewModel[];
  theme: string;

  availablePipelines: SelectOption[];
  availableCrmTypes: SelectOption[];
  availableDisplayBy: SelectOption[];
  availableGroups: SelectOption[];
  availableDateFilterModes: SelectOption[];
  availableColumns: SelectOption[];
  availableFilters: FilterViewModel[];
  availablePipelineStages: AvailablePipelineStages[];
};

export type AvailablePipelineStages = {
  pipelineId: string;
  pipelineStages: SelectOption[];
};

export type DynamicFilterViewModel = {
  filterId: string;
  values: string[];
};

export type FilterViewModel = {
  id: string;
  filterType: FilterType;
  values?: SelectOption[];
  autocomplete?: boolean;
  autocompleteUrl?: string;
  name: string;
};

export type FunnelChart = {
  data: FunnelDataPoint[];
  chartElementId: string;
  legendElementId: string;
  title: string;
};

export type FunnelChartData = {
  libraryItemId: string;
  wrapperElementId: string;
  charts: FunnelChart[];
  pipelineLabel?: string;
};

export type FunnelDataPoint = {
  label: string;
  value: number;
  formattedvalue: string;
};

export type ReportColumn = {
  name: string;
  cssClass?: string;
};

export type ReportRow = {
  cells: ReportCell[];
};

export type ReportCell = {
  value: string;
  cssClass?: string;
};

export type ReportData = {
  columns: ReportColumn[];
  rows: ReportRow[];
};

export type WebhookSearch = {
    id: number;
    url: string;
    name: string;
    isActive: boolean;
    createdDate: Date;
    createdById: string;
    createdByName: string;
    updatedDate: Date;
    updatedById: string;
    updatedByName: string;
    deletedDate: Date | null;
    deletedById: string | null;
    deletedByName: string | null;
    isDeleted: boolean;
    eventCount: number;
}

export type BaseWebhook = {
  url: string;
  name: string;
  description?: string;
  isActive: boolean;
  eventIds: number[];
}

export type CreateWebhook = BaseWebhook;

export type UpdateWebhook = BaseWebhook & {
  id: number;
}

export type Webhook = {
  id: number;
  url: string;
  name: string;
  description?: string;
  isActive: boolean,
  createdDate: Date,
  createdBy: string,
  createdByName: string,
  updatedDate: Date,
  updatedBy: string,
  updatedByName: string,
  deletedDate: Date | null,
  deletedBy: string | null,
  deletedByName: string | null,
  isDeleted: boolean,
  eventIds: number[];
  secret: string;
  secretGeneratedDate: Date;
};

export type WebhookEvent = {
  id: number;
  name: string;
  description?: string;
  displayOrder: number;
  categoryId: number;
  categoryName: string;
};


export type ResponseDto<T> = {
  data: T;
  meta: ResponseMeta;
};

export type ResponseListDto<T> = {
  data: T[];
  meta: ResponseMeta;
};

export type ResponseMeta = {
  message: string;
};

export type PaginatedResponse<T> = {
    data: T[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

// PascalCase property names match the legacy notify.js localStorage cache format
// and the SignalR push format, so no conversion is needed when reading/writing
// the shared cache that both React and legacy pages consume.

export type NotificationSource = {
  SourceType: number;
  SourceID: string;
};

export type NotificationOwner = {
  Name: string;
};

export type NotificationItem = {
  Id: string;
  Subject: string;
  Body?: string;
  SendDate?: string;
  Source?: NotificationSource;
  Owner?: NotificationOwner;
};

export type TaskReminderCompany = {
  CompanyName: string;
};

export type TaskReminderItem = {
  // Note: tasks use 'ID' (all caps) — matches legacy notify.js and SignalR shape
  ID: string;
  Subject?: string;
  DueDate?: string;
  Priority?: number;
  Company?: TaskReminderCompany;
  RecMaster?: string;
  RecurrenceIndex?: number;
};

export type NotificationsResponse = {
  notifications: NotificationItem[];
  tasks: TaskReminderItem[];
};

export type SearchResultItem = {
  id: string;
  label: string;
  value: string;
  category: string;
  target: string;
};

export type ActivityItem = {
  id: string;
  description: string;
  syncWithCalendar: boolean;
};

export type ActivityGroup = {
  groupName: string;
  items: ActivityItem[];
};

export type ActivitiesResponse = {
  defaultActivityId: string;
  groups: ActivityGroup[];
};

export type Contact = {
  id: string;
  name: string;
  isPrimary: boolean;
};

export type SaveQuickTaskRequest = {
  taskId: string | null;
  customerId: string | null;
  contactId: string | null;
  subject: string;
  body: string;
  defaultActivityId: string | null;
  projectId: string | null;
  completeTask: boolean;
  redirectUrl: string;
};

export type SaveQuickTaskResponse = {
  success: boolean;
  taskId: string | null;
  editUrl: string | null;
  errorMessage: string | null;
};

export type CompanyDetails = {
  companyId: string;
  accountNo: string;
  companyName: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  telephone: string;
  website: string;
  webLink: string;
  email: string;
};

export type ContactDetails = {
  contactId: string;
  contactName: string;
  title: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  salutation: string;
  jobDescription: string;
  department: string;
  status: string;
  isPrimary: boolean;
  fax: string;
  comments: string;
  companyId: string;
  accountNo: string;
  companyName: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  contactOwnAddress1: string;
  contactOwnAddress2: string;
  contactOwnAddress3: string;
  contactOwnAddress4: string;
  contactOwnTown: string;
  contactOwnCounty: string;
  contactOwnPostcode: string;
  contactOwnCountry: string;
  companyAddress1: string;
  companyAddress2: string;
  companyAddress3: string;
  companyAddress4: string;
  companyTown: string;
  companyCounty: string;
  companyPostcode: string;
  companyCountry: string;
  email: string;
  workTelephone: string;
  companyTelephone: string;
  mobileTelephone: string;
  homeTelephone: string;
  createdByName: string;
  createdDate: string;
  modifiedByName: string;
  modifiedDate: string;
  userDefinedGroups: ContactUserDefinedGroup[];
  fieldOptions: {
    contactName: ContactFieldOptions;
    jobDescription: ContactFieldOptions;
    department: ContactFieldOptions;
    email: ContactFieldOptions;
    telephoneOffice: ContactFieldOptions;
    telephoneHome: ContactFieldOptions;
    telephoneMobile: ContactFieldOptions;
    fax: ContactFieldOptions;
    address: ContactFieldOptions;
    comments: ContactFieldOptions;
  };
};

export type ContactUserDefinedGroup = {
  name: string;
  items: ContactUserDefinedField[];
};

export type ContactUserDefinedField = {
  name: string;
  value: string;
  itemId: number;
  format: string;
  decimalPlaces: number | null;
  isLookup: boolean;
  lookupValues: string[] | null;
  isAutoComplete: boolean;
  isMandatory: boolean;
  isReadOnly: boolean;
};

export type ContactNote = {
  id: string;
  subject: string;
  body: string;
  contactName: string;
  modifiedDate: string;
  modifiedByName: string;
};

export type SearchType = {
  id: string;
  description: string;
  parentOptionGroup: string | null;
  isOptionGroupItem: boolean;
};

export type SelectionItem = {
  text: string;
  value: string;
};

export type Selections = {
  globalSelections: SelectionItem[];
  personalSelections: SelectionItem[];
};

export type ProjectAliases = {
  project: string;
  projects: string;
  projectGeneric: string;
  projectsGeneric: string;
  epic: string;
  epics: string;
  workflow: string;
  workflows: string;
};

export type AppRoutes = {
  root: string;
  ajaxAutoComplete: string;
  ajaxUtilities: string;
  dashboard: string;
  homeDeleteSelection: string;
  superEnquiry: string;
  loadingEnquiry: string;
  handlersFavouriteEnquiry: string;
  addTaskPopup: string;
  contactLookup: string;
  productLookup: string;
  prodGrpLookup: string;
  alertLookup: string;
  companyLookup: string;
  analysisLookup: string;
  sales: string;
  companyMaintLookup: string;
  myHome: string;
  notify: string;
  notificationsList: string;
  viewTasks: string;
  myDiary: string;
  addProspect: string;
  userPreferences: string;
  accountSettings: string;
  logout: string;
};

export type LegacyShellData = {
  enabled: boolean;
  canToggle?: boolean;
  paths: string[];
};

export type WhatsNewEntry = {
  id: string;
  area: string;
  icon: string;
  title: string;
  summary: string;
  points: string[];
};

export type LayoutData = {
  whatsNew?: WhatsNewEntry[] | null;
  legacyShell?: LegacyShellData | null;
  username: string;
  account: string;
  userId: string;
  clientId: string;
  databaseId: string;
  customerMode: boolean;
  theme: string;
  newTaskDefaultFull: boolean | null;
  erpConfigured: boolean;
  isAiChatAvailable: boolean;
  visibleMaintenanceAreas: string[];
  searchTypes: SearchType[];
  selectedSearchType: string;
  projectAliases: ProjectAliases;
  appRoutes: AppRoutes;
  fullLogoUrl: string | null;
  miniLogoUrl: string | null;
  userInitials: string;
  userAvatarColor: string;
  googleMapsApiKey: string | null;
};

export type MenuItem = {
  id: string;
  label: string;
  icon: string | null;
  url: string | null;
  type: "link" | "popup" | "dropdown" | "favourites" | "pipelines" | "settings";
  section?: string | null;
  badge?: string | null;
  startsNewGroup?: boolean;
  hint?: string | null;
  keywords?: string | null;
  isSelected: boolean;
  isVisible: boolean;
  children: MenuItem[] | null;
  popupWindowName: string | null;
  popupWidth: number | null;
  popupHeight: number | null;
  testId?: string;
};

export type FavouriteCategory = {
  id: string;
  name: string;
  colour: string;
  foreColour: string;
  subscriptionType: "Personal" | "Subscribed";
  favourites: { id: string; name: string; description: string }[];
};

export type FavouritesMenuGroup = {
  groupLabel: string;
  categories: FavouriteCategory[];
};

export type PipelineSummary = {
  id: string;
  description: string;
  name: string;
};

export type PipelinesMenu = {
  selectedPipelineId: string | null;
  pipelines: PipelineSummary[];
};

export type AnalysisType = "Company" | "Product" | "History" | "Opportunity" | "Project";

export type AnalysisValue = {
  code: string;
  description: string;
};

export type AnalysisItem = {
  index: number;
  shortName: string;
  fullName: string;
  defaultCode: string | null;
  defaultDescription: string | null;
  isAutoComplete: boolean;
  /** Null when isAutoComplete is true */
  values: AnalysisValue[] | null;
};

export type UserDefinedField = {
  id: string;
  itemId: number;
  name: string;
  /** "String" | "Integer" | "Float" | "Date" | "Boolean" */
  format: string;
  /** Number of decimal places. Only set when format is "Float". */
  decimalPlaces: number | null;
  isLookup: boolean;
  lookupValues: string[] | null;
  isAutoComplete: boolean;
  isMandatory: boolean;
  isReadOnly: boolean;
};

export type UserDefinedGroup = {
  id: string;
  name: string;
  items: UserDefinedField[];
};

export type AnalysisValueSubmit = {
  index: number;
  code: string;
  description: string;
};

export type UserDefinedValueSubmit = {
  itemId: number;
  value: string;
};

export type AccountNoAutoGenerateState = "Disabled" | "Optional" | "Mandatory";

export type QuickCreateProspectOptions = {
  accountNoAutoGenerate: AccountNoAutoGenerateState;
  showComments: boolean;
  showTelephone: boolean;
  showFax: boolean;
  showEmail: boolean;
  showWebAddress: boolean;
  showAddress: boolean;
  analysisItems: AnalysisItem[];
  userDefinedGroups: UserDefinedGroup[];
};

export type QuickCreateProspectRequest = {
  companyName: string;
  accountNo?: string;
  telephone?: string;
  fax?: string;
  email?: string;
  webAddress?: string;
  comments?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  town?: string;
  county?: string;
  country?: string;
  postcode?: string;
  analysisValues?: AnalysisValueSubmit[];
  userDefinedValues?: UserDefinedValueSubmit[];
};

export type QuickCreateProspectResponse = {
  success: boolean;
  companyId: string | null;
  companyName: string | null;
  errorMessage: string | null;
};

export type ContactFieldOptions = {
  isMandatory: boolean;
  isReadOnly: boolean;
};

export type QuickCreateContactOptions = {
  contactName: ContactFieldOptions;
  jobDescription: ContactFieldOptions;
  department: ContactFieldOptions;
  email: ContactFieldOptions;
  telephoneOffice: ContactFieldOptions;
  telephoneHome: ContactFieldOptions;
  telephoneMobile: ContactFieldOptions;
  fax: ContactFieldOptions;
  address: ContactFieldOptions;
  comments: ContactFieldOptions;
  companyAddress1: string | null;
  companyAddress2: string | null;
  companyAddress3: string | null;
  companyAddress4: string | null;
  companyTown: string | null;
  companyCounty: string | null;
  companyCountry: string | null;
  companyPostcode: string | null;
  userDefinedGroups: UserDefinedGroup[];
};

export type QuickCreateContactRequest = {
  customerId: string;
  firstName: string;
  surname: string;
  preferredName?: string;
  title?: string;
  salutation?: string;
  jobDescription?: string;
  department?: string;
  email?: string;
  telephoneOffice?: string;
  telephoneHome?: string;
  telephoneMobile?: string;
  fax?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  town?: string;
  county?: string;
  country?: string;
  postcode?: string;
  comments?: string;
  primaryContact: number;
  userDefinedValues?: UserDefinedValueSubmit[];
};

export type QuickCreateContactResponse = {
  success: boolean;
  contactId: string | null;
  contactName: string | null;
  errorMessage: string | null;
};

export enum AuthStatus {
  Success = "success",
  Requires2Fa = "requires2fa",
  RequiresPasswordUpdate = "requiresPasswordUpdate",
  Error = "error",
}

export type AuthResult = {
  status: AuthStatus;
  errorMessage?: string;
  redirectUrl?: string;
  passwordDays?: number;
  passwordStatus?: "expired" | "expiring";
};

export type LoginRequest = {
  account: string;
  username: string;
  password: string;
  customerMode: boolean;
  timezone: number;
  returnUrl?: string;
};

export type VerifyCodeRequest = {
  code: string;
  timezone: number;
  rememberBrowser: boolean;
  returnUrl?: string;
};

export type ForgotPasswordRequest = {
  account: string;
  email: string;
};

export type ResetPasswordRequest = {
  account: string;
  email: string;
  token: string;
  password: string;
};

export type UpdatePasswordRequest = {
  currentPassword: string;
  newPassword: string;
};

export type SsoInitiateRequest = {
  account: string;
  customerMode: boolean;
  timezone: number;
};

export type SsoAuthUrlResponse = {
  authUrl: string;
};

export type ConfirmEmailResponse = {
  isConfirmed: boolean;
};
